import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem as SelectMenuItem,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalHospital as ClinicIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast from 'react-hot-toast';

import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { clinicService, Clinic, ClinicCreate } from '../services/clinicService';
import servicesService, { Service, MEDICATION_TYPE_LABELS } from '../services/servicesService';
import { formatDate } from '../utils/dateUtils';
import RequirePermission from '../components/common/RequirePermission';
import { useCrudHandlers } from '../hooks/useCrudHandlers';
import { useUserTimezone } from '../hooks/useUserTimezone';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { clinicColumns } from '../config/clinicColumns';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'align',
  'list', 'bullet',
  'link',
];

const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const ClinicsPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { currentTenant } = useTenant();
  const userTimezone = useUserTimezone();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // UI State
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingClinic, setEditingClinic] = useState<Clinic | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clinicToDelete, setClinicToDelete] = useState<Clinic | null>(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [pendingLogoKey, setPendingLogoKey] = useState<string | null>(null);
  const [pendingLogoPreview, setPendingLogoPreview] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [services, setServices] = useState<Service[]>([]);

  const { register, handleSubmit, reset, setValue, control, formState: { errors } } = useForm<ClinicCreate>();

  // Table hook callbacks
  const getSortValue = useCallback((clinic: Clinic, key: string): any => {
    switch (key) {
      case 'name': return clinic.name.toLowerCase();
      case 'idPrefix': return clinic.id_prefix.toLowerCase();
      case 'createdAt': return clinic.created_at;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((clinic: Clinic): string[] => {
    return [
      clinic.name,
      clinic.id_prefix,
      clinic.tenant_id.toString(),
      clinic.email_sender || '',
    ];
  }, []);

  const table = useTablePage<Clinic>({
    entityType: 'clinics',
    columns: clinicColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    getSearchText,
  });

  const crud = useCrudHandlers<Clinic>({
    table,
    createFn: (data) => clinicService.createClinic({ ...data, tenant_id: currentTenant!.id }),
    updateFn: (id, data) => clinicService.updateClinic(id as number, data),
    deleteFn: (id) => clinicService.deleteClinic(id as number),
    entityName: 'Clinic',
    onSuccess: () => { setAddDialogOpen(false); setEditingClinic(null); reset(); if (pendingLogoPreview) URL.revokeObjectURL(pendingLogoPreview); setPendingLogoKey(null); setPendingLogoPreview(null); },
    onDeleteSuccess: () => { setDeleteDialogOpen(false); setClinicToDelete(null); },
  });

  // Load data
  useEffect(() => {
    if (currentUser?.user_privilege?.config && currentTenant) {
      table.loadData(() => clinicService.getClinics(currentTenant.id));
      servicesService.getAll(currentTenant.id).then(setServices).catch(() => setServices([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, currentTenant]);

  const handleEdit = () => {
    if (table.selectedItem) {
      setEditingClinic(table.selectedItem);
      reset({
        name: table.selectedItem.name,
        id_prefix: table.selectedItem.id_prefix,
        timezone: table.selectedItem.timezone,
        language: table.selectedItem.language,
        default_country_code: table.selectedItem.default_country_code,
        state: table.selectedItem.state || '',
        sms_sender: table.selectedItem.sms_sender || '',
        email_sender: table.selectedItem.email_sender || '',
        email_sender_name: table.selectedItem.email_sender_name || '',
        email_footer: table.selectedItem.email_footer || '',
        sms_footer: table.selectedItem.sms_footer || '',
        clinic_contact_info_email: table.selectedItem.clinic_contact_info_email || '',
        clinic_contact_info_sms: table.selectedItem.clinic_contact_info_sms || '',
        logo: table.selectedItem.logo || '',
        service_id: table.selectedItem.service_id ?? null,
      });
      setActiveTab(0);
      setAddDialogOpen(true);
    }
    table.handleMenuClose();
  };

  const handleDelete = () => {
    if (table.selectedItem) {
      setClinicToDelete(table.selectedItem);
      setDeleteDialogOpen(true);
    }
    table.handleMenuClose();
  };

  const handleAddClinic = () => {
    setEditingClinic(null);
    reset({
      name: '',
      id_prefix: '',
      timezone: 'UTC',
      language: 'en',
      default_country_code: '+44',
      state: '',
      sms_sender: '',
      email_sender: '',
      email_sender_name: '',
      email_footer: '',
      sms_footer: '',
      clinic_contact_info_email: '',
      clinic_contact_info_sms: '',
      logo: '',
      service_id: null,
    });
    setActiveTab(0);
    setAddDialogOpen(true);
  };

  const handleLogoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editingClinic) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File is too large. Maximum size is 2MB.');
      return;
    }

    setLogoLoading(true);
    const previewUrl = URL.createObjectURL(file);
    try {
      const { s3_key } = await clinicService.uploadLogo(editingClinic.id, file);
      setPendingLogoKey(s3_key);
      setValue('logo', s3_key);
      setPendingLogoPreview(previewUrl);
      setEditingClinic({ ...editingClinic, logo: s3_key });
      toast.success('Logo ready — click Update to save');
    } catch (err) {
      URL.revokeObjectURL(previewUrl);
      toast.error('Failed to upload logo');
    } finally {
      setLogoLoading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    if (!editingClinic) return;
    setLogoLoading(true);
    try {
      await clinicService.deleteLogo(editingClinic.id);
      setValue('logo', '');
      setEditingClinic({ ...editingClinic, logo: null });
      table.loadData(() => clinicService.getClinics(currentTenant!.id));
      toast.success('Logo removed successfully');
    } catch (err) {
      toast.error('Failed to remove logo');
    } finally {
      setLogoLoading(false);
    }
  };

  const onSubmit = (data: ClinicCreate) => {
    if (!currentTenant) {
      toast.error('No tenant selected');
      return;
    }
    crud.handleCreateOrUpdate({
      ...data,
      service_id: data.service_id ?? null,
      ...(pendingLogoKey ? { logo: pendingLogoKey } : {}),
    }, editingClinic);
    if (pendingLogoPreview) URL.revokeObjectURL(pendingLogoPreview);
    setPendingLogoKey(null);
    setPendingLogoPreview(null);
  };

  const handleConfirmDelete = () => {
    crud.handleDelete(clinicToDelete);
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    return [
      { label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: handleEdit },
      { label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: handleDelete, color: 'error' as const },
    ];
  };

  // Column value renderer
  const getColumnValue = (clinic: Clinic, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name':
        return (
          <Box display="flex" alignItems="center">
            <ClinicIcon sx={{ mr: 2, color: 'text.secondary' }} />
            <Box>
              <Typography variant="body2" fontWeight="medium">{clinic.name}</Typography>
              <Typography variant="caption" color="text.secondary">ID: {clinic.id}</Typography>
            </Box>
          </Box>
        );
      case 'idPrefix':
        return <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{clinic.id_prefix}</Typography>;
      case 'settings':
        return (
          <Box>
            <Typography variant="caption" display="block">Timezone: {clinic.timezone}</Typography>
            <Typography variant="caption" display="block">Language: {clinic.language}</Typography>
          </Box>
        );
      case 'contact':
        return (
          <Box>
            {clinic.email_sender && <Typography variant="caption" display="block">{clinic.email_sender}</Typography>}
            {clinic.sms_sender && <Typography variant="caption" display="block">SMS: {clinic.sms_sender}</Typography>}
            {!clinic.email_sender && !clinic.sms_sender && <Typography variant="caption" color="text.secondary">N/A</Typography>}
          </Box>
        );
      case 'emailFooter': {
        const text = clinic.email_footer ? stripHtml(clinic.email_footer) : '';
        return <Typography variant="body2" color={text ? 'inherit' : 'text.secondary'}>{text ? (text.length > 40 ? `${text.slice(0, 40)}…` : text) : '—'}</Typography>;
      }
      case 'smsFooter': {
        const text = clinic.sms_footer ? clinic.sms_footer.trim() : '';
        return <Typography variant="body2" color={text ? 'inherit' : 'text.secondary'}>{text ? (text.length > 40 ? `${text.slice(0, 40)}…` : text) : '—'}</Typography>;
      }
      case 'createdAt':
        return <Typography variant="body2">{formatDate(clinic.created_at, userTimezone)}</Typography>;
      default: return '';
    }
  };

  // Mobile card renderer
  const renderMobileCard = (clinic: Clinic) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} sx={{ pr: 5 }}>
        <Box display="flex" alignItems="center" flex={1}>
          <ClinicIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.5rem' }} />
          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
            {clinic.name}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ pl: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          ID Prefix: {clinic.id_prefix}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Timezone: {clinic.timezone} | Language: {clinic.language}
        </Typography>
        {clinic.email_sender && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Email: {clinic.email_sender}
          </Typography>
        )}
        {clinic.sms_sender && (
          <Typography variant="body2" color="text.secondary">
            SMS: {clinic.sms_sender}
          </Typography>
        )}
      </Box>
    </>
  );

  return (
  <RequirePermission check={!!currentUser?.user_privilege?.config} featureName="clinic management">
    <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading clinics:">
      <DataTableToolbar
        {...table.toolbarProps}
        title="Clinic Management"
        searchPlaceholder="Search clinics by name, ID prefix, tenant, or email..."
        isAdmin={!!currentUser?.user_privilege?.config}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClinic}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add Clinic
          </Button>
        }
      />

      {isMobile ? (
        <MobileCardGrid
          data={table.sortedData}
          renderCard={renderMobileCard}
          onMenuClick={table.handleRowClick}
          getItemKey={(c) => c.id}
          emptyIcon={<ClinicIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
          emptyMessage="No clinics found"
        />
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={table.sortedData}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            onRowClick={table.handleRowClick}
            getRowKey={(c) => c.id}
          />
        </DataTableContainer>
      )}

      {/* Row Context Menu */}
      <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

      {/* Manage Columns Modal */}
      <ManageColumnsModal {...table.manageColumnsProps} />

      {/* Save View Modal */}
      <SaveViewModal {...table.saveViewModalProps} />

      {/* Add/Edit Clinic Dialog */}
      <ClinicDialog
        open={addDialogOpen}
        onClose={() => { setAddDialogOpen(false); if (pendingLogoPreview) URL.revokeObjectURL(pendingLogoPreview); setPendingLogoKey(null); setPendingLogoPreview(null); }}
        title={editingClinic ? 'Edit Clinic' : 'Add New Clinic'}
        maxWidth="md"
        onSubmit={handleSubmit(onSubmit)}
        actions={
          <>
            <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
              {editingClinic ? 'Update' : 'Create'}
            </Button>
            <Button onClick={() => { setAddDialogOpen(false); if (pendingLogoPreview) URL.revokeObjectURL(pendingLogoPreview); setPendingLogoKey(null); setPendingLogoPreview(null); }} sx={CLINIC_DIALOG_SX.cancelButton}>
              Cancel
            </Button>
          </>
        }
      >
        <Box>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tab label="General" />
            <Tab label="Service" />
            <Tab label="Branding & Footers" />
          </Tabs>

          {/* Tab 0: General */}
          {activeTab === 0 && <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>

            {/* Clinic Name */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Clinic Name</Typography>
              <TextField
                {...register('name', { required: 'Clinic name is required' })}
                fullWidth size="small"
                error={!!errors.name}
                helperText={errors.name?.message as string}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {/* ID Prefix */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>ID Prefix</Typography>
              <TextField
                {...register('id_prefix', {
                  required: 'ID prefix is required',
                  pattern: { value: /^[A-Z0-9-]+$/, message: 'Uppercase letters, numbers and hyphens only' }
                })}
                fullWidth size="small"
                error={!!errors.id_prefix}
                helperText={errors.id_prefix?.message as string || 'e.g. CLN-001'}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {/* Timezone */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Timezone</Typography>
              <Controller name="timezone" control={control} defaultValue="UTC" rules={{ required: true }}
                render={({ field }) => (
                  <Select {...field} fullWidth sx={CLINIC_DIALOG_SX.select}>
                    <SelectMenuItem value="UTC">UTC</SelectMenuItem>
                    <SelectMenuItem value="America/New_York">America/New York (EST)</SelectMenuItem>
                    <SelectMenuItem value="America/Chicago">America/Chicago (CST)</SelectMenuItem>
                    <SelectMenuItem value="America/Los_Angeles">America/Los Angeles (PST)</SelectMenuItem>
                    <SelectMenuItem value="Europe/London">Europe/London (GMT)</SelectMenuItem>
                    <SelectMenuItem value="Europe/Paris">Europe/Paris (CET)</SelectMenuItem>
                    <SelectMenuItem value="Asia/Jerusalem">Asia/Jerusalem (IST)</SelectMenuItem>
                    <SelectMenuItem value="Asia/Tokyo">Asia/Tokyo (JST)</SelectMenuItem>
                  </Select>
                )}
              />
            </Box>

            {/* Language */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Language</Typography>
              <Controller name="language" control={control} defaultValue="en" rules={{ required: true }}
                render={({ field }) => (
                  <Select {...field} fullWidth sx={CLINIC_DIALOG_SX.select}>
                    <SelectMenuItem value="en">English</SelectMenuItem>
                    <SelectMenuItem value="he">Hebrew</SelectMenuItem>
                    <SelectMenuItem value="es">Spanish</SelectMenuItem>
                    <SelectMenuItem value="fr">French</SelectMenuItem>
                    <SelectMenuItem value="de">German</SelectMenuItem>
                  </Select>
                )}
              />
            </Box>

            {/* Default Country Code */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Country Code</Typography>
              <Controller name="default_country_code" control={control} defaultValue="+44" rules={{ required: true }}
                render={({ field }) => (
                  <Select {...field} fullWidth sx={CLINIC_DIALOG_SX.select}>
                    <SelectMenuItem value="+44">+44 (United Kingdom)</SelectMenuItem>
                    <SelectMenuItem value="+972">+972 (Israel)</SelectMenuItem>
                    <SelectMenuItem value="+1">+1 (US/Canada)</SelectMenuItem>
                    <SelectMenuItem value="+33">+33 (France)</SelectMenuItem>
                    <SelectMenuItem value="+49">+49 (Germany)</SelectMenuItem>
                    <SelectMenuItem value="+34">+34 (Spain)</SelectMenuItem>
                    <SelectMenuItem value="+39">+39 (Italy)</SelectMenuItem>
                    <SelectMenuItem value="+61">+61 (Australia)</SelectMenuItem>
                  </Select>
                )}
              />
            </Box>

            {/* State */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>State</Typography>
              <Controller name="state" control={control} defaultValue=""
                render={({ field }) => (
                  <Select {...field} fullWidth displayEmpty sx={CLINIC_DIALOG_SX.select}>
                    <SelectMenuItem value="">— None —</SelectMenuItem>
                    <SelectMenuItem value="UK">United Kingdom</SelectMenuItem>
                    <SelectMenuItem value="IL">Israel</SelectMenuItem>
                  </Select>
                )}
              />
            </Box>

            {/* Email Sender */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Email Sender</Typography>
              <TextField
                {...register('email_sender', {
                  pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' }
                })}
                fullWidth size="small" type="email"
                error={!!errors.email_sender}
                helperText={errors.email_sender?.message as string || 'From address'}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {/* Email Sender Name */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Email Sender Name</Typography>
              <TextField
                {...register('email_sender_name')}
                fullWidth size="small"
                helperText="Display name for outgoing emails"
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {/* SMS Sender */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>SMS Sender</Typography>
              <TextField
                {...register('sms_sender')}
                fullWidth size="small"
                helperText="Phone number or sender ID"
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

          </Box>}

          {/* Tab 1: Service */}
          {activeTab === 1 && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                Service
              </Typography>
              <Controller
                name="service_id"
                control={control}
                defaultValue={null}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                    fullWidth
                    displayEmpty
                    sx={CLINIC_DIALOG_SX.select}
                  >
                    <SelectMenuItem value="">— Not set —</SelectMenuItem>
                    {services.map((svc) => (
                      <SelectMenuItem key={svc.id} value={svc.id}>
                        {svc.name} ({MEDICATION_TYPE_LABELS[svc.medication_type]})
                      </SelectMenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              The service defines the medication behaviour and the ordered meeting flow for this clinic's patients (Config → Services).
            </Typography>
          </Box>}

          {/* Tab 2: Branding & Footers */}
          {activeTab === 2 && <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>

            {/* Email Footer — spans full width */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, gridColumn: '1 / -1' }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Email Footer</Typography>
              <Box sx={{
                '& .ql-container': { overflow: 'visible' },
                '& .ql-editor': { height: '160px', overflowY: 'auto' },
              }}>
                <Controller name="email_footer" control={control} defaultValue=""
                  render={({ field }) => (
                    <ReactQuill
                      value={field.value || ''}
                      onChange={field.onChange}
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ marginBottom: '50px' }}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* SMS Footer — spans full width */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, gridColumn: '1 / -1' }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>SMS Footer</Typography>
              <TextField
                {...register('sms_footer')}
                fullWidth size="small"
                multiline rows={3}
                helperText="Plain text appended to outgoing SMS messages"
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {/* Email Contact Info — spans full width */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, gridColumn: '1 / -1' }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Email Contact Info</Typography>
              <Box sx={{
                '& .ql-container': { overflow: 'visible' },
                '& .ql-editor': { height: '160px', overflowY: 'auto' },
              }}>
                <Controller name="clinic_contact_info_email" control={control} defaultValue=""
                  render={({ field }) => (
                    <ReactQuill
                      value={field.value || ''}
                      onChange={field.onChange}
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ marginBottom: '50px' }}
                    />
                  )}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">Available in templates as {'{clinic_contact_info}'}</Typography>
            </Box>

            {/* SMS Contact Info — spans full width */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, gridColumn: '1 / -1' }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>SMS Contact Info</Typography>
              <TextField
                {...register('clinic_contact_info_sms')}
                fullWidth size="small"
                multiline rows={3}
                helperText="Plain text version of contact info, used in SMS via {clinic_contact_info}"
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {/* Logo — spans full width */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, gridColumn: '1 / -1' }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Logo</Typography>
              <Box>
                {editingClinic ? (
                  <>
                    {(editingClinic.logo || pendingLogoPreview) && (
                      <Box mb={1}>
                        <img
                          src={pendingLogoPreview ?? `${clinicService.getLogoUrl(editingClinic.id)}?t=${new Date(editingClinic.updated_at).getTime()}`}
                          alt="Clinic logo"
                          style={{ width: 120, height: 60, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 4 }}
                        />
                      </Box>
                    )}
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={handleLogoFileChange}
                    />
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Button variant="outlined" size="small" disabled={logoLoading} onClick={() => logoInputRef.current?.click()}>
                        {logoLoading ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                      {editingClinic.logo && (
                        <Button variant="outlined" size="small" color="error" disabled={logoLoading} onClick={handleDeleteLogo}>
                          Remove Logo
                        </Button>
                      )}
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">Save the clinic first to upload a logo</Typography>
                )}
              </Box>
            </Box>

          </Box>}
        </Box>
      </ClinicDialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        entityType="Clinic"
        entityName={clinicToDelete?.name}
      />
    </TablePageLayout>
  </RequirePermission>
  );
};

export default ClinicsPage;
